import styled from 'styled-components';
import { colors, tokens } from '../../../utils';

// T22.231 (phone UX audit UX-31, MEASURED live web-stg.iyuga.net bell popover,
// 390x844, bundle main.dd06f65e.js): in the bell dropdown there is no wheel
// thumbnail column (RightSection is page-only), so the text column runs all the
// way to the 12px unread dot — while the absolutely-positioned KebabButton sits
// on top of the row's right edge (right:8px, 32px wide, and always visible on
// touch via @media (hover:none)). 3 of 10 rows had their first text line drawn
// UNDER the kebab ("QA joined a7-T311-mv03xceo as a⋮"). Reserve exactly the
// strip the kebab covers, minus what the row's own right padding and the dot
// already occupy, plus a 4px breathing gap.
export const ROW_PADDING_X = 14;
export const GREEN_DOT_SIZE = 12;
export const KEBAB_INSET = 8;
const KEBAB_TEXT_GAP = 4;
export const KEBAB_TEXT_CLEARANCE =
  KEBAB_INSET + tokens.iconOverlay.minHitTarget + KEBAB_TEXT_GAP - ROW_PADDING_X - GREEN_DOT_SIZE;

// QA-1014 (Gordon 2026-08-24, FB-style redesign): the notification row.
//  · Part 5 — the gray inter-row divider is ELIMINATED. Only the warning type
//    keeps its 2px red bottom rule (a semantic alert border, not a divider);
//    every normal row has no border, so rows read as one continuous FB-style list.
//  · Part 6 — vertical margins are TIGHTER: padding 20px -> 8px 14px, and the heavy
//    per-row drop-shadow is removed (a dividerless FB list is flat, not carded).
//  · Part 7 — on hover the whole row lifts to a PURPLE highlight (PrimaryPurple100).
//  · Part 8 — an UNREAD row carries a PURPLE tint background (PrimaryPurple50), a
//    READ row is white. This replaces the old teal-ish #FAFBFF unread wash.
//  · position:relative anchors the on-hover kebab in the upper-right corner.
export const NotificationContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) =>
    props.isUnread ? colors.PrimaryPurple50 : '#FFFFFF'};
  cursor: pointer;
  padding: 8px ${ROW_PADDING_X}px; /* Part 6: tighter vertical rhythm between rows */
  width: 100%; /* Makes sure it takes full width */
  border-bottom: ${(props) => (props.isWarning ? '2px solid red' : 'none')};
  transition: background-color 0.12s ease;

  /* Part 7: on-hover purple highlight (stronger than the unread tint so an
     unread row still visibly responds to hover). */
  &:hover,
  &:focus-within {
    background-color: ${colors.PrimaryPurple100};
  }

  /* Part 3: the kebab is hidden until the row is hovered or focused within
     (keyboard users must still be able to reach it), FB-style. */
  &:hover .notif-row-kebab,
  &:focus-within .notif-row-kebab {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const LeftSection = styled.div`
  display: flex;
  gap: 12px;
  flex: 1; /* Allows text section to take available space without affecting image */
  padding-right: ${(props) => (props.$kebabClearance ? `${KEBAB_TEXT_CLEARANCE}px` : '0')};
  /* N-08 residual (measured live web-stg 2026-07-28, bell panel): a flex item
     defaults to min-width:auto, so this column refused to shrink below its
     content and no clamp/ellipsis inside it could ever engage. min-width:0 is
     the canonical flex-overflow release and matches the deck's own row spec
     (concepts/notification-center-elevated/02-bell-dropdown-grid.html, rule
     .nd-txt, which sets min-width:0). */
  min-width: 0;
`;
export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* Was max-width:80vw, which is meaningless inside the 380px bell panel:
     measured live 2026-07-28, a single unbreakable 220-char token grew this
     column to 1152px (= 80vw at a 1440 viewport) — 3x the panel it lives in.
     Bound the column to its ACTUAL parent instead, and release min-width so the
     line clamps below can do their job. Measured after: 1152px -> 214px. */
  min-width: 0;
  max-width: 100%;
`;

export const ImageContainer = styled.div`
  position: relative;
  width: 50px; /* Increased size */
  height: 50px;
`;

export const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;

export const IconOverlay = styled.div`
  position: absolute;
  bottom: -5px;
  right: -4px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; /* Prevents extra background */
`;

export const IconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensures the image fills the space */
  border-radius: 50%;
`;

export const RightSection = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 20px;
`;

export const WheelImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  object-position: center;
  border-radius: 10px;
  flex-shrink: 0;

  /* PrimaryWheelPlaceholder.svg is a full iYuga wheel (petals + sparkles).
     object-fit:cover on the 60x60 slot cropped the bottom petals. Real
     wheel_preview rasters stay cover; only this placeholder uses contain. */
  &[data-placeholder-wheel='true'] {
    object-fit: contain;
  }
`;

// kelly-notif-follow-row-no-wheel-slot-0915: a non-wheel row (e.g. FOLLOW) has
// no thumbnail to show, but the column must still hold the same 60x60 box a
// wheel row's <WheelImage> occupies — same dimensions, invisible, so the
// right-hand rail is a straight vertical edge regardless of row type.
export const WheelSlotSpacer = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin: 8px 0;
`;

export const StyledButton = styled.button`
  background-color: ${(props) => (props.variant === 'accept' ? '#084394' : 'transparent')};
  color: ${(props) => (props.variant === 'accept' ? 'white' : 'grey')};
  border: ${(props) => (props.variant === 'accept' ? 'none' : '1px solid #e1e1e1')};
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background-color: ${(props) => (props.variant === 'accept' ? '#053b85' : '#f9f9f9')};
  }
`;

// Right-aligned slot holding the shared Follow + Message affordance (UserActions,
// compact ⋮ variant) for rows whose actor is a real person. flex-shrink:0 keeps
// the ⋮ from being squeezed by the flex-1 text section on narrow rows.
export const ActorActionsSlot = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
`;

// kelly-notif-page-thumb-column-ragged-12px-0915: this row is display:flex
// with LeftSection at flex:1, so removing the dot entirely on a read row (the
// old `isUnread && <GreenDot/>` gate) shrinks total flex-item width by 12px —
// LeftSection's flex:1 absorbs that freed space and every sibling to its
// right (the wheel thumbnail, the actor-actions slot) slides 12px right.
// Always render the dot's box so its 12px is always reserved in the flex
// row; only its paint is conditional on unread state.
export const GreenDot = styled.div`
  width: ${GREEN_DOT_SIZE}px;
  height: ${GREEN_DOT_SIZE}px;
  background-color: #77cccd;
  border-radius: 50%;
  align-self: center;
  visibility: ${(props) => (props.isUnread ? 'visible' : 'hidden')};
`;

// QA-1014 part 2/3: the per-row kebab (3-dot) menu trigger, pinned to the UPPER-
// RIGHT corner and revealed only on row hover/focus (opacity flips to 1 via the
// NotificationContainer hover rule above). Keyboard focus makes it appear too, so
// it is never permanently unreachable for non-mouse users.
//
// fix-resp0919-touch-targets-below-32px-both-env: MEASURED live on
// web-dev.iyuga.net/notifications (390x844, bundle main.778bf9f4.js) at a
// real 0x0 hit area (elementFromPoint walk from center) before any
// hover/focus-within -- opacity:0 + pointer-events:none by default, and
// touch has no "hover first" gesture, so this control was UNREACHABLE on
// touch, not merely undersized. `@media (hover: none)` keeps it visible and
// tappable on touch-primary devices (mouse/keyboard behavior unchanged); the
// box also grows to the shared `tokens.iconOverlay.minHitTarget` floor
// (32px) that every other icon-overlay control in the app observes (see
// Capture's MenuKebabBtn, src/pages/Capture/styles.js).
export const KebabButton = styled.button`
  position: absolute;
  top: 6px;
  right: ${KEBAB_INSET}px;
  width: ${tokens.iconOverlay.minHitTarget}px;
  height: ${tokens.iconOverlay.minHitTarget}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #565556;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, background-color 0.12s ease;

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
    /* T22.230 (UX-30, measured web-stg main.dd06f65e @390): on a touch-primary
       device the 32px compact box is under the 44px finger target. Grow it to
       the kebab "row" hit area and shift top/right by half the growth so the
       glyph's centre stays exactly where it was (22px down, 24px in). */
    top: ${6 - (tokens.kebab.hitArea.row - tokens.iconOverlay.minHitTarget) / 2}px;
    right: ${8 - (tokens.kebab.hitArea.row - tokens.iconOverlay.minHitTarget) / 2}px;
    width: ${tokens.kebab.hitArea.row}px;
    height: ${tokens.kebab.hitArea.row}px;
  }

  &:hover {
    background-color: ${colors.PrimaryPurple100};
    color: ${colors.PrimaryPurple};
  }
  /* Keep it visible while its menu is open even if the pointer leaves the row. */
  &[aria-expanded='true'] {
    opacity: 1;
    pointer-events: auto;
    background-color: ${colors.PrimaryPurple100};
    color: ${colors.PrimaryPurple};
  }
  &:focus-visible {
    opacity: 1;
    pointer-events: auto;
    outline: 2px solid ${colors.PrimaryPurple};
    outline-offset: 1px;
  }
`;

// QA-1014 part 4: Follow / Message on the RIGHT of the row, VERTICALLY STACKED
// (FB friend-request layout). Only rendered for FOLLOW notifications, so the feed
// never fires a per-row follow-status GET on every unrelated row.
// kelly-notif-follow-slot-column-x-0915: `inSlot` renders this control INSIDE the
// row's wheel-thumbnail column (RightSection) instead of as an EXTRA in-flow
// sibling beside it.
//
// MEASURED LIVE on web-dev.iyuga.net (main.5e7a2989.js, uid 12675,
// /notifications, 1440x900): as a separate sibling this column occupies
// 44px + margin-left 10px + margin-right 24px = 78px of the row's flex line.
// NotificationContainer is display:flex with LeftSection at flex:1, so those
// 78px exist ONLY on FOLLOW rows — and every fixed-width item to their left is
// pushed 78px further LEFT on a follow row than on a wheel row. Simulated
// against that same live layout, PR #5598's new 60x60 slot landed at x=968 on
// follow rows while wheel thumbnails sat at x=1046 — the slot existed but the
// column was still not a straight vertical edge.
//
// In-slot the control becomes the column's content (the follow-row counterpart
// of a wheel row's thumbnail): same 60px box, no extra margins, so a follow
// row's flex line matches a wheel row's item-for-item and the column shares one
// x. align-items:center (not stretch) keeps the buttons their natural width, so
// only their position inside the reserved column changes, never their size.
export const RightActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  ${(props) =>
    props.inSlot
      ? `
  align-items: center;
  width: 60px;
  margin: 0;
`
      : `
  align-items: stretch;
  margin-left: 10px;
  /* Leave clearance for the upper-right kebab so the two never overlap. */
  margin-right: 24px;
`}
`;
